import axios, { type AxiosResponse } from "axios";

/**
 * Low-level client for backend-auth owned 2FA.
 *
 * This module intentionally mirrors django-two-factor's HTML wizard behavior:
 * - login + setup state lives in server session/cookies
 * - FE posts wizard form fields and interprets returned HTML/redirects
 * - FE does not implement OTP logic; backend remains source of truth
 */

const apiBase = process.env.REACT_APP_API_BASE_URL ?? "";
const siteOrigin = new URL(apiBase).origin;

export const MFA_LOGIN_URL = `${apiBase}auth/login/`;
export const MFA_SETUP_URL = `${siteOrigin}/account/two_factor/setup/`;
export const MFA_QR_SVG_URL = `${siteOrigin}/account/two_factor/qrcode/`;

export type KnoxSession = {
  token: string;
  expiry: string;
  user: {
    email: string;
    last_login: string | null;
    first_name: string;
    last_name: string;
    date_joined: string;
    is_staff: boolean;
  };
};

export type LoginInterpretResult =
  | { kind: "knox"; session: KnoxSession }
  | { kind: "totp" }
  | { kind: "backup" }
  | { kind: "setup" }
  | { kind: "auth_failed" }
  | { kind: "html"; note?: string };

const wizardClient = axios.create({
  withCredentials: true,
  validateStatus: () => true,
  responseType: "text",
  transformResponse: [(d) => d],
});

// Fallback for cross-origin deployments where document.cookie can't read the API origin's csrftoken.
let _csrfTokenCache: string | null = null;

function csrfFromCookie(): string | null {
  const m = document.cookie.match(/(?:^|;\s*)csrftoken=([^;]*)/);
  return m ? decodeURIComponent(m[1]) : _csrfTokenCache;
}

export function responseUrl(res: AxiosResponse): string {
  const xhr = res.request as XMLHttpRequest | undefined;
  return xhr?.responseURL ?? "";
}

export function isTwoFactorSetupCompleteResponse(url: string, html: string): boolean {
  const u = url.toLowerCase();
  if (u.includes("setup/complete") || u.includes("setup_complete")) return true;
  return html.toLowerCase().includes("successfully enabled two-factor");
}

export function tryParseKnoxSession(raw: string): KnoxSession | null {
  const t = raw.trim();
  if (!t.startsWith("{")) return null;
  try {
    const o = JSON.parse(t) as unknown;
    if (o && typeof o === "object" && "token" in o && typeof (o as { token: unknown }).token === "string") {
      return o as KnoxSession;
    }
  } catch {
    /* ignore */
  }
  return null;
}

export function readCurrentStep(html: string, stepPrefix: string): string | null {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const input = doc.querySelector(`input[name="${stepPrefix}-current_step"]`) as HTMLInputElement | null;
  return input?.value ?? null;
}

export function looksLikeLoginAuthFailure(html: string): boolean {
  return (
    readCurrentStep(html, "login_view") === "auth" &&
    (html.includes("errorlist") || html.includes("non_field_errors") || /Please enter a correct/i.test(html))
  );
}

function findWizardForm(doc: Document, stepPrefix: string): HTMLFormElement | null {
  const forms = Array.from(doc.querySelectorAll("form"));
  return (
    forms.find((f) => f.querySelector(`input[name="${stepPrefix}-current_step"]`)) ??
    forms.find((f) => f.getAttribute("method")?.toLowerCase() === "post") ??
    null
  );
}

export function serializeWizardForm(html: string, stepPrefix: string): URLSearchParams {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const form = findWizardForm(doc, stepPrefix);
  const params = new URLSearchParams();
  if (!form) return params;

  const elements = form.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(
    "input[name], select[name], textarea[name], button[name]"
  );
  elements.forEach((el) => {
    const name = el.name;
    if (!name) return;
    if (el instanceof HTMLInputElement) {
      if (el.type === "checkbox" || el.type === "radio") {
        if (el.checked) params.append(name, el.value);
        return;
      }
      if (el.type === "submit") {
        params.append(name, el.value);
        return;
      }
      if (el.type === "button") return;
    }
    if (el instanceof HTMLButtonElement && el.type === "submit") {
      params.append(name, el.value);
      return;
    }
    params.append(name, el.value);
  });
  return params;
}

// Prevent accidental "Back" navigation when serializing full wizard forms.
export function stripWizardGotoStep(body: URLSearchParams): void {
  body.delete("wizard_goto_step");
}

export async function postForm(url: string, body: URLSearchParams): Promise<AxiosResponse<string>> {
  const csrf = csrfFromCookie();
  return wizardClient.post<string>(url, body.toString(), {
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      ...(csrf ? { "X-CSRFToken": csrf } : {}),
    },
  });
}

export async function bootstrapMfaLogin(): Promise<void> {
  const res = await wizardClient.get(MFA_LOGIN_URL);
  const headerToken = res.headers['x-csrf-token'] as string | undefined;
  if (headerToken) _csrfTokenCache = headerToken;
}

export async function postLoginAuthStep(username: string, password: string): Promise<AxiosResponse<string>> {
  const csrf = csrfFromCookie();
  if (!csrf) await bootstrapMfaLogin();
  const token = csrfFromCookie();
  if (!token) throw new Error("CSRF token not available. Enable cookies for the API origin.");

  const body = new URLSearchParams({
    csrfmiddlewaretoken: token,
    "login_view-current_step": "auth",
    "auth-username": username,
    "auth-password": password,
  });
  return postForm(MFA_LOGIN_URL, body);
}

export async function postLoginOtpStep(otp: string): Promise<AxiosResponse<string>> {
  const csrf = csrfFromCookie();
  if (!csrf) throw new Error("CSRF token not available.");
  const body = new URLSearchParams({
    csrfmiddlewaretoken: csrf,
    "login_view-current_step": "token",
    "token-otp_token": otp.replace(/\s/g, ""),
  });
  return postForm(MFA_LOGIN_URL, body);
}

export async function postLoginBackupStep(code: string): Promise<AxiosResponse<string>> {
  const csrf = csrfFromCookie();
  if (!csrf) throw new Error("CSRF token not available.");
  const body = new URLSearchParams({
    csrfmiddlewaretoken: csrf,
    "login_view-current_step": "backup",
    "backup-otp_token": code.trim(),
  });
  return postForm(MFA_LOGIN_URL, body);
}

export async function getSetupPage(): Promise<AxiosResponse<string>> {
  return wizardClient.get<string>(MFA_SETUP_URL);
}

export async function submitSetupForm(body: URLSearchParams): Promise<AxiosResponse<string>> {
  return postForm(MFA_SETUP_URL, body);
}

export function interpretLoginResponse(res: AxiosResponse<string>): LoginInterpretResult {
  const raw = res.data ?? "";
  const knox = tryParseKnoxSession(raw);
  if (knox) return { kind: "knox", session: knox };
  if (looksLikeLoginAuthFailure(raw)) return { kind: "auth_failed" };

  const url = responseUrl(res);
  if (url.includes("two_factor/setup") || raw.includes("account/two_factor/setup")) return { kind: "setup" };
  if (isTwoFactorSetupCompleteResponse(url, raw)) return { kind: "html", note: "setup_complete" };

  const step = readCurrentStep(raw, "login_view");
  if (step === "token" || raw.includes("token-otp_token")) return { kind: "totp" };
  if (step === "backup" || raw.includes("backup-otp_token")) return { kind: "backup" };
  return { kind: "html" };
}

// Setup responses can be redirect HTML, wizard HTML, or (custom) Knox JSON.
export function interpretSetupPostResponse(res: AxiosResponse<string>): LoginInterpretResult {
  const raw = res.data ?? "";
  const url = responseUrl(res);
  if (isTwoFactorSetupCompleteResponse(url, raw)) return { kind: "html", note: "setup_complete" };
  const knox = tryParseKnoxSession(raw);
  if (knox) return { kind: "knox", session: knox };
  const step = readCurrentStep(raw, "setup_view");
  if (step === "generator") return { kind: "html", note: "generator_error" };
  if (step) return { kind: "html", note: `wizard_step:${step}` };
  return { kind: "html" };
}

export async function fetchQrSvgBlobUrl(): Promise<string> {
  const csrf = csrfFromCookie();
  const res = await axios.get<Blob>(MFA_QR_SVG_URL, {
    withCredentials: true,
    responseType: "blob",
    validateStatus: () => true,
    headers: csrf ? { "X-CSRFToken": csrf } : {},
  });
  if (res.status >= 400) throw new Error(`QR request failed (${res.status}).`);
  return URL.createObjectURL(res.data);
}
